import { Capacitor } from '@capacitor/core';

/**
 * PWA install + update plumbing.
 * `beforeinstallprompt` can fire before React mounts, so we capture it at module load
 * (imported from index.js) and let components subscribe.
 */

let deferredPrompt = null;
let updateRegistration = null;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => fn());
}

export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

export function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function isIos() {
  const ua = window.navigator.userAgent;
  // iPadOS 13+ reports itself as Mac; detect via touch support.
  return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

export function initPwaListeners() {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Stop the mini-infobar; we show our own Install button instead.
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit();
  });
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getPwaState() {
  return {
    canPrompt: Boolean(deferredPrompt),
    updateReady: Boolean(updateRegistration),
  };
}

/** @returns {Promise<'accepted'|'dismissed'|'unavailable'>} */
export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';
  const prompt = deferredPrompt;
  deferredPrompt = null;
  prompt.prompt();
  const { outcome } = await prompt.userChoice;
  emit();
  return outcome;
}

/** Called by serviceWorkerRegistration when a new version is waiting. */
export function setUpdateReady(registration) {
  updateRegistration = registration;
  emit();
}

export function applyUpdate() {
  const waiting = updateRegistration?.waiting;
  if (!waiting) {
    window.location.reload();
    return;
  }
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
    once: true,
  });
  waiting.postMessage({ type: 'SKIP_WAITING' });
}
